import React from 'react';
import { useParams, useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { PageHeader, SectionCard, EmptyState, StatusPill, ProjectTabBar } from '@/components/portal/PortalUI';
import { Button } from '@shared/components/ui/button';
import { useToast } from '@shared/components/ui/use-toast';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@shared/components/ui/dialog';
import {
  Users, Trash2, Loader2, CheckCircle2, Clock, UserX, ClipboardList,
} from 'lucide-react';
import { supabase } from '@shared/supabaseClient';
import { InviteTeamMemberDialog } from '@/components/portal/InviteTeamMemberDialog';

const PortalProjectTeam = () => {
  const { projectId } = useParams();
  const { session } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [project, setProject]                 = React.useState(null);
  const [teamMembers, setTeamMembers]         = React.useState([]);
  const [inductionStatus, setInductionStatus] = React.useState({});
  const [loading, setLoading]                 = React.useState(true);
  const [inviteOpen, setInviteOpen]           = React.useState(false);
  const [confirmDelete, setConfirmDelete]     = React.useState(null);
  const [deletingId, setDeletingId]           = React.useState(null);

  const email = session?.person?.email;
  const [isProjectLead, setIsProjectLead] = React.useState(false);

  React.useEffect(() => {
    if (projectId) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Auto-open invite dialog when navigated from setup guide
  React.useEffect(() => {
    if (location.state?.openInviteDialog) {
      setInviteOpen(true);
      // Clear state so re-renders don't re-open
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const { data: proj } = await supabase
        .from('projects')
        .select('id, project_name, site_id, project_lead_email, depots:site_id(name)')
        .eq('id', projectId)
        .single();
      setProject(proj);
      setIsProjectLead(proj?.project_lead_email?.toLowerCase() === email?.toLowerCase());

      const { data: members } = await supabase
        .from('project_team_members')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });
      setTeamMembers(members || []);

      if (proj?.site_id && members?.length) {
        const map = {};
        for (const m of members) {
          const { data: subs } = await supabase
            .from('induction_submissions')
            .select('id, status')
            .eq('person_email', m.person_email)
            .eq('depot_id', proj.site_id)
            .in('status', ['invited', 'in_progress', 'submitted', 'approved']);
          const total     = subs?.length || 0;
          const completed = subs?.filter(s => s.status === 'approved' || s.status === 'submitted').length || 0;
          map[m.id] = { total, completed, allDone: total > 0 && completed === total };
        }
        setInductionStatus(map);
      }
    } catch (err) {
      console.error('[PortalProjectTeam] load failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvited = () => {
    toast({ title: 'Invitation sent', description: 'They will receive a portal link by email.' });
    loadAll();
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete.id);
    try {
      const { data, error } = await supabase.rpc('portal_remove_team_member', {
        p_member_id:       confirmDelete.id,
        p_project_id:      projectId,
        p_requester_email: email,
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error || 'Failed to remove');
      toast({ title: 'Removed', description: `${confirmDelete.person_name} removed from project.` });
      setConfirmDelete(null);
      loadAll();
    } catch (err) {
      toast({ title: 'Error', description: err.message || 'Could not remove member.', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  const inductionBadge = (memberId) => {
    const s = inductionStatus[memberId];
    if (!s) return <span className="text-xs text-muted-foreground">Not started</span>;
    if (s.allDone)
      return (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
          <CheckCircle2 className="h-3.5 w-3.5" /> Inducted
        </span>
      );
    if (s.completed > 0)
      return (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
          <Clock className="h-3.5 w-3.5" /> In progress ({s.completed}/{s.total})
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground">
        <Clock className="h-3.5 w-3.5" /> Awaiting induction
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const allInducted = teamMembers.length > 0 && teamMembers.every(m => inductionStatus[m.id]?.allDone);

  return (
    <div className="space-y-6">
      <ProjectTabBar projectId={projectId} isProjectLead={isProjectLead} />
      <PageHeader
        title="Project Team"
        subtitle={project?.project_name ?? projectId}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`/portal/projects/${projectId}/setup`)}>
              <ClipboardList className="h-4 w-4 mr-2" />
              Setup Guide
            </Button>
            {isProjectLead && (
              <Button size="sm" onClick={() => setInviteOpen(true)}>
                <Users className="h-4 w-4 mr-2" />
                Invite team member
              </Button>
            )}
          </div>
        }
      />

      {teamMembers.length > 0 && (
        <div className={`rounded-xl border px-4 py-3 flex items-center gap-3 ${allInducted ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
          {allInducted
            ? <CheckCircle2 className="h-4 w-4 text-green-700 flex-shrink-0" />
            : <Clock className="h-4 w-4 text-amber-700 flex-shrink-0" />}
          <p className={`text-sm font-semibold ${allInducted ? 'text-green-900' : 'text-amber-900'}`}>
            {allInducted
              ? `All ${teamMembers.length} team member${teamMembers.length !== 1 ? 's' : ''} have completed their induction.`
              : `${teamMembers.filter(m => inductionStatus[m.id]?.allDone).length} of ${teamMembers.length} member${teamMembers.length !== 1 ? 's' : ''} inducted.`}
          </p>
        </div>
      )}

      {teamMembers.length ? (
        <SectionCard title={`${teamMembers.length} member${teamMembers.length !== 1 ? 's' : ''}`}>
          <div className="space-y-3">
            {teamMembers.map((member) => (
              <div
                key={member.id}
                className="flex flex-col gap-3 rounded-lg border border-border/60 bg-muted/40 p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{member.person_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{member.person_email}</p>
                  {member.person_company && (
                    <p className="text-xs text-muted-foreground">{member.person_company}</p>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {inductionBadge(member.id)}
                  <StatusPill
                    tone={member.status === 'active' ? 'success' : 'warning'}
                    label={member.status === 'active' ? 'Active' : 'Invited'}
                  />
                  {isProjectLead && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                      onClick={() => setConfirmDelete(member)}
                      disabled={deletingId === member.id}
                    >
                      {deletingId === member.id
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Trash2 className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      ) : (
        <EmptyState
          icon={Users}
          title="No team members yet"
          description="Invite the people working with you on this project. Each person will receive a portal link to complete their induction."
        />
      )}

      <InviteTeamMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        projectId={projectId}
        onSuccess={handleInvited}
        skipDonePhase
        onDone={() => navigate(`/portal/projects/${projectId}/setup`)}
      />

      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-destructive" /> Remove team member?
            </DialogTitle>
            <DialogDescription>
              This will remove <strong>{confirmDelete?.person_name}</strong> from the project.
              Their induction record will not be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={!!deletingId}>
              {deletingId ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalProjectTeam;
